import { GlobalStyles } from '@mui/system'
import { Analytics } from '@core/services/mixpanel'
import { useDrawer } from '@core/context/DrawerContext'
import { UserInputsModule } from '@core/modules/user-inputs'
import { ScansService } from '@core/services/api-gateway'
import { generateToast } from '@/ui/components/toasting'
import { ActionButton } from '@/ui/components/buttons/action'
import { AddDomain } from '@/ui/drawer/digitalFootprint/addDomain'
import useAddDomain from '@/ui/drawer/digitalFootprint/addDomain/useAddDomain'
import DomainDiscovery from '@/ui/drawer/digitalFootprint/domainDiscovery/domainDiscovery'
import {
    Body,
    Footer,
    Wrapper,
    drawerScrollFixStyles,
} from './AddDomainDrawer.styles'

const AddDomainDrawer = () => {
    const { drawerState, setDrawerState } = useDrawer()
    const {
        loading,
        addDomains,
        isSaveEnabled,
        handleOnDomainChange,
        handleOnDomainDiscoveryDeleted,
        handleOnDomainDiscoveryRestore,
        domainDiscoveryDomainsArray,
        domains,
        onDomainAdd,
        inputDisabled,
        onDomainEdit,
        removeTagFromDomains,
        domainCheckValue,
        addDomainsError,
        domainInputRef,
    } = useAddDomain()

    // Keyed on isDrawerOpen, not mount: reopening during the close animation
    // keeps this body mounted, and the provider has already reset onClose.
    useEffect(() => {
        if (!drawerState.isDrawerOpen) return

        setDrawerState((prevState) => ({
            ...prevState,
            onClose: () => Analytics.event('SEM::add_domain/s_canceled'),
        }))
    }, [drawerState.isDrawerOpen])

    const closeAfterSuccess = useCallback(() => {
        setDrawerState((prevState) => ({
            ...prevState,
            isDrawerOpen: false,
            drawerContentType: null,
        }))
    }, [setDrawerState])

    const handleOnActionButtonClick = useCallback(async () => {
        const eventProps = {
            added_count: domains.length,
            removed_count: domainDiscoveryDomainsArray.filter(
                (domain) => domain.deleted
            ).length,
        }

        const isAddDomainSucceeded = await addDomains()

        if (isAddDomainSucceeded) {
            Analytics.event('SEM::added_domain/s_successfully', eventProps)
            Analytics.event('SEM::user_added_domains_from_discovery')
            generateToast({
                type: 'success',
                data: `Domains have been added successfully and a new scan is in progress. Information will be updated on the dashboard once the scan is complete.`,
                testId: 'toast-domains-added',
            })
            ScansService.getScans(2000).catch(console.error)
            UserInputsModule.pollProcesses()
            closeAfterSuccess()
        } else {
            Analytics.event('SEM::add_domain/s_failed', eventProps)
            generateToast({
                type: 'error',
                data: `Failed to add domain`,
            })
        }
    }, [domains, domainDiscoveryDomainsArray, addDomains, closeAfterSuccess])

    return (
        <Wrapper className="add-domain-drawer">
            <GlobalStyles styles={drawerScrollFixStyles} />
            <Body>
                <DomainDiscovery
                    {...{
                        handleOnDomainChange,
                        handleOnDomainDiscoveryDeleted,
                        handleOnDomainDiscoveryRestore,
                        domainDiscoveryDomainsArray,
                    }}
                />
                <AddDomain
                    {...{
                        domains,
                        onDomainAdd,
                        inputDisabled,
                        onDomainEdit,
                        removeTagFromDomains,
                        domainCheckValue,
                        addDomainsError,
                        domainInputRef,
                    }}
                />
            </Body>
            <Footer>
                <ActionButton
                    className="action-button"
                    dataTestId="save-button"
                    loading={loading}
                    text="Save"
                    disabled={loading || !isSaveEnabled}
                    onClick={handleOnActionButtonClick}
                />
            </Footer>
        </Wrapper>
    )
}

export default AddDomainDrawer
